/**
 * Фильтры для описателя. Модуль для работы с API.
 * @module definitionFilters
 */

import { maxPerPage } from "@/constants";

/**
 * Создание группы фильтров для страницы с таблицей объектов
 * @param {Object} item - данные группы
 * @param {string} item.definition_uuid - идентификатор описателя, к которому создается новая группа фильтров
 * @param {string} item.idx - порядковый номер (нумерация с 0)
 * @param {string} item.caption - название группы
 * @returns {Promise} - созданная группа
 * @throws {Error} - ошибка при создании данных
 */
export const createGroup = async (item) => {
  try {
    const response = await fetch("/api/definitionfiltergroup", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при создании данных. ${data.error || "Попробуйте снова"}`
      );
    }

    return data.group;
  } catch (err) {
    throw err;
  }
};

/**
 * Обновление группы фильтров для страницы с таблицей объектов
 * @param {Object} item - данные группы
 * @param {string} item.uuid - идентификатор группы
 * @param {string} item.idx - порядковый номер (нумерация с 0)
 * @param {string} item.caption - название группы
 * @returns {Promise} - обновленная группа
 * @throws {Error} - ошибка при обновлении данных
 */
export const updateGroup = async (item) => {
  try {
    const response = await fetch("/api/definitionfiltergroup", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при обновлении данных. ${data.error || "Попробуйте снова"}`
      );
    }

    return data.group;
  } catch (err) {
    throw err;
  }
};

/**
 * Удаление группы фильтров
 * @param {Object} item - данные группы
 * @param {string} item.uuid - идентификатор группы
 * @returns {Promise} - удаленная группа
 * @throws {Error} - ошибка при удалении данных
 */
export const deleteGroup = async (item) => {
  try {
    const response = await fetch("/api/definitionfiltergroup", {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при удалении данных. ${data.error || "Попробуйте снова"}`
      );
    }

    return data.group;
  } catch (err) {
    throw err;
  }
};

/**
 * Получение списка полей, которые не задействованы ни в одной группе
 * @param {Object} params - Параметры запроса
 * @param {string} params.definition_uuid - Идентификатор описателя
 * @param {string} params.search - поиск по названию
 * @param {string} params.sort - сортировка
 * @param {string} params.page - номер страницы
 * @param {string} params.perpage - количество элементов на странице
 * @returns {Promise} - список описателей объектов
 * @throws {Error} - ошибка при получении данных
 */
export const getAvailableFields = async (params) => {
  const queryString = new URLSearchParams({
    ...params,
    page: 1,
    perpage: maxPerPage,
  }).toString();

  try {
    const response = await fetch(
      `/api/definitionfilters/available-fields?${queryString}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`
      );
    }

    return data.fields;
  } catch (err) {
    throw err;
  }
};

/**
 * Добавление фильтра в группу
 * @param {Object} item - новый фильтр
 * @param {string} item.definition_uuid - идентификатор описателя, к которому создается новый фильтр
 * @param {string} item.group - идентификатор группы, к которой создается новый фильтр
 * @param {string} item.field_uuid - идентификатор поля, к которому создается новый фильтр
 * @param {string} item.caption - название фильтра (если не прислали, автоматически станет = имя поля)
 * @param {string} item.idx - порядковый номер (нумерация с 0)
 * @returns {Promise} - обновленная группа
 * @throws {Error} - ошибка при добавлении данных
 */
export const createFilter = async (item) => {
  try {
    const response = await fetch("/api/definitionfilter", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error("Ошибка при добавлении данных. Попробуйте снова.");
    }

    return data.filter;
  } catch (err) {
    throw err;
  }
};

/**
 * Обновление фильтра
 * @param {Object} item - данные фильтра
 * @param {string} item.uuid - идентификатор фильтра
 * @param {string} item.group - идентификатор группы, к которой создается новый фильтр
 * @param {string} item.caption - название фильтра (если не прислали, автоматически станет = имя поля)
 * @param {string} item.idx - порядковый номер (нумерация с 0)
 * @returns {Promise} - обновленный фильтр
 * @throws {Error} - ошибка при обновлении данных
 */
export const updateFilter = async (item) => {
  try {
    const response = await fetch("/api/definitionfilter", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при обновлении данных. ${data.error || "Попробуйте снова"}`
      );
    }

    return data.filter;
  } catch (err) {
    throw err;
  }
};

/**
 * Удаление фильтра
 * @param {Object} item - данные фильтра
 * @param {string} item.uuid - идентификатор фильтра
 * @returns {Promise} - удаленный фильтр
 * @throws {Error} - ошибка при удалении данных
 */
export const deleteFilter = async (item) => {
  try {
    const response = await fetch("/api/definitionfilter", {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при удалении данных. ${data.error || "Попробуйте снова"}`
      );
    }

    return data.filter;
  } catch (err) {
    throw err;
  }
};

/**
 * Синхронизировать фильтры описателя с его элементной (объектной) формой
 * @param {Object} definition_uuid - идентификатор описателя
 * @returns {Promise} - синхронизированные фильтры
 * @throws {Error} - ошибка при синхронизации данных
 */
export const synchronizeFilters = async (definition_uuid) => {
  try {
    const response = await fetch("/api/definitionfilters/synchronize", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ definition_uuid }),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error("Ошибка при добавлении данных. Попробуйте снова.");
    }

    return data;
  } catch (err) {
    throw err;
  }
};
